import React, { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Send, MessageSquare, UserCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [users, setUsers] = useState([]);
  const bottomRef = useRef(null);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
    } catch {}
  };

  const loadThread = async (tid) => {
    setActive(tid);
    const { data } = await api.get(`/messages/thread/${tid}`);
    setMessages(data);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const loadUsers = async () => {
    if (user?.role === "clinician") {
      const { data } = await api.get("/facilities");
      setUsers(data.map((f) => ({ id: f.user_id, name: f.facility_name, role: "facility" })));
    } else {
      try {
        const { data } = await api.get("/clinicians");
        setUsers(data.map((c) => ({ id: c.user_id, name: c.name, role: "clinician" })));
      } catch {}
    }
  };

  useEffect(() => { loadThreads(); loadUsers(); }, [user]);

  const startThread = (otherId) => {
    const tid = [user.id, otherId].sort().join("::");
    setActive(tid);
    setMessages([]);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    const [a, b] = active.split("::");
    const to = a === user.id ? b : a;
    try {
      await api.post("/messages", { to_user_id: to, body: text, thread_id: active });
      setText("");
      loadThread(active);
      loadThreads();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  return (
    <div className="h-[calc(100vh-130px)]" data-testid="messages-page">
      <div className="h-full grid lg:grid-cols-[320px_1fr] gap-4">
        <aside className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-200">
            <div className="overline text-trital-red">Conversations</div>
            <div className="mt-1 font-display font-bold text-lg">Inbox</div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {threads.length === 0 && <div className="p-4 text-sm text-slate-500">No conversations yet.</div>}
            {threads.map((t) => (
              <button key={t.thread_id} onClick={() => loadThread(t.thread_id)} className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${active === t.thread_id ? "bg-trital-medical-blue-soft/50" : ""}`} data-testid={`thread-${t.thread_id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900 text-sm flex items-center gap-2"><UserCircle2 className="w-4 h-4 text-trital-navy" /> {t.other_name}</div>
                  {t.unread > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-trital-red text-white font-bold">{t.unread}</span>}
                </div>
                <div className="text-xs text-slate-500 mt-1 truncate">{t.last_message}</div>
              </button>
            ))}
          </div>
          <div className="p-3 border-t border-slate-200">
            <div className="overline text-slate-500 mb-1">Start new</div>
            <select onChange={(e) => e.target.value && startThread(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
              <option value="">Select a contact…</option>
              {users.filter((u) => u.id).map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
        </aside>
        <section className="rounded-2xl bg-white border border-slate-200 shadow-card flex flex-col">
          {active ? (
            <>
              <div className="px-5 py-3 border-b border-slate-200 font-display font-bold text-slate-900">Conversation</div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {messages.map((m) => {
                  const mine = m.from_user_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${mine ? "bg-trital-navy text-white" : "bg-slate-100 text-slate-900"}`}>
                        <div className="text-[10px] uppercase tracking-wider opacity-70">{m.from_name}</div>
                        <div className="text-sm mt-0.5 leading-relaxed">{m.body}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
                {messages.length === 0 && <div className="text-sm text-slate-500 text-center">Start the conversation 👋</div>}
              </div>
              <form onSubmit={send} className="p-3 border-t border-slate-200 flex gap-2">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 px-4 py-2.5 rounded-full border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none text-sm" data-testid="message-input" />
                <button className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full bg-trital-navy text-white font-semibold text-sm btn-lift" data-testid="message-send"><Send className="w-4 h-4" /> Send</button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-2">
              <MessageSquare className="w-10 h-10 text-trital-navy/30" />
              <div className="font-display font-bold">Select or start a conversation</div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
